import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Tables } from '@/integrations/supabase/types';
import { Staff } from '@/components/SchedulerTypes';
import { useSchedulerState } from '@/hooks/useSchedulerState';
import { useAuth } from '@/contexts/AuthContext';
import RatingPerformanceReport from '@/components/RatingPerformanceReport';
import { format, endOfDay, startOfDay, subDays, isWithinInterval, differenceInCalendarDays } from 'date-fns';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar } from '@/components/ui/calendar';
import { CalendarIcon, Info } from 'lucide-react';
import { formatDateToString, toSingaporeTime } from '@/lib/dateUtils';

type StaffTodo = Tables<'staff_todos'>;

const RatingButtons: React.FC<{
  value?: number | null;
  onChange: (rating: number) => void;
}> = ({ value, onChange }) => {
  const options = [1, 2, 3, 4, 5];
  return (
    <div className="flex gap-1" title="Click any number to rate or change rating">
      {options.map((n) => (
        <Button
          key={n}
          type="button"
          size="sm"
          variant={value === n ? 'default' : 'outline'}
          className="h-7 px-2 text-xs hover:scale-110 transition-transform"
          onClick={() => onChange(n)}
        >
          {n}
        </Button>
      ))}
    </div>
  );
};

const TaskPerformanceReviewPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';

  const { staff, staffTodos, updateStaffTodo } = useSchedulerState();

  // Admin-only gate
  if (!isAdmin) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-gray-700 mb-2">Access Denied</h2>
          <p className="text-gray-500">This page is restricted to admin users.</p>
          <Button className="mt-4" onClick={() => navigate('/tasks')}>Back to Tasks</Button>
        </div>
      </div>
    );
  }

  const [activeTab, setActiveTab] = useState<'review' | 'report'>('review');
  const [selectedStaffId, setSelectedStaffId] = useState<string>('all');
  const [reportRangeType, setReportRangeType] = useState<'7days' | '30days' | 'custom'>('30days');
  const [reportCustom, setReportCustom] = useState<{ from: Date | undefined; to: Date | undefined }>({ from: undefined, to: undefined });
  const [lateReasonModal, setLateReasonModal] = useState<{
    show: boolean;
    task: StaffTodo | null;
  }>({ show: false, task: null });
  const [reviewerComment, setReviewerComment] = useState<string>('');
  const [editingTaskId, setEditingTaskId] = useState<string | null>(null);

  const activeStaff: Staff[] = staff.filter((s) => s.status === 'active');

  // Helper function to check if task is late
  const isTaskLate = (task: StaffTodo) => {
    if (!task.deadline || !task.completed_at || task.status !== 'completed') {
      return false;
    }
    const deadline = new Date(task.deadline);
    const completed = new Date(task.completed_at);
    return formatDateToString(completed) > formatDateToString(deadline);
  };

  // Helper function to calculate days late
  const calculateDaysLate = (task: StaffTodo) => {
    if (!task.deadline || !task.completed_at) return 0;
    const deadline = new Date(task.deadline);
    const completed = new Date(task.completed_at);
    const diffDays = differenceInCalendarDays(
      toSingaporeTime(completed),
      toSingaporeTime(deadline)
    );
    return Math.max(0, diffDays);
  };

  // Completed tasks only; sorted by completion date desc
  const completedTodos: StaffTodo[] = useMemo(() => {
    const list = staffTodos.filter((t) => t.status === 'completed');
    return list.sort((a, b) => {
      const aDate = a.completed_at ? new Date(a.completed_at).getTime() : 0;
      const bDate = b.completed_at ? new Date(b.completed_at).getTime() : 0;
      return bDate - aDate;
    });
  }, [staffTodos]);

  const [page, setPage] = useState<number>(1);
  const pageSize = 25;

  const visibleTodos: StaffTodo[] = useMemo(() => {
    if (selectedStaffId === 'all') return completedTodos;
    return completedTodos.filter((t) => t.staff_id === selectedStaffId);
  }, [completedTodos, selectedStaffId]);

  // Reset to first page when filter changes
  React.useEffect(() => {
    setPage(1);
  }, [selectedStaffId]);

  const totalPages = Math.max(1, Math.ceil(visibleTodos.length / pageSize));
  const paginatedTodos = useMemo(() => {
    const start = (page - 1) * pageSize;
    return visibleTodos.slice(start, start + pageSize);
  }, [visibleTodos, page, pageSize]);

  const getStaffName = (id: string) => activeStaff.find((s) => s.id === id)?.name || 'Unknown';

  const handleRate = (todo: StaffTodo, rating: number) => {
    // Leverage existing update flow; include rating as an extra column
    const updated = {
      ...(todo as any),
      rating,
      reviewer_id: user?.id,
      reviewed_at: new Date().toISOString()
    } as any;
    (updateStaffTodo as any)(updated);
    setEditingTaskId(todo.id);
  };

  const handleUpdateReviewerComment = (todo: StaffTodo, comment: string) => {
    const updated = {
      ...(todo as any),
      reviewer_comment: comment.trim() || null,
      reviewer_id: user?.id,
      reviewed_at: new Date().toISOString()
    } as any;
    (updateStaffTodo as any)(updated);
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <h2 className="text-lg sm:text-xl font-semibold">Review Performance</h2>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => navigate('/tasks')}>Back to Tasks</Button>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as any)} className="w-full">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="review">Review</TabsTrigger>
          <TabsTrigger value="report">Performance Report</TabsTrigger>
        </TabsList>

        <TabsContent value="review" className="mt-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Staff column */}
            <Card>
              <CardHeader>
                <CardTitle className="text-sm">Staff</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-1">
                  <Button
                    variant={selectedStaffId === 'all' ? 'default' : 'ghost'}
                    className="w-full justify-between"
                    onClick={() => setSelectedStaffId('all')}
                  >
                    View All
                    <Badge variant="secondary">{completedTodos.length}</Badge>
                  </Button>
                  {activeStaff
                    .slice()
                    .sort((a, b) => a.name.localeCompare(b.name))
                    .map((s) => {
                      const count = completedTodos.filter((t) => t.staff_id === s.id).length;
                      return (
                        <Button
                          key={s.id}
                          variant={selectedStaffId === s.id ? 'default' : 'ghost'}
                          className="w-full justify-between"
                          onClick={() => setSelectedStaffId(s.id)}
                        >
                          <span className="truncate text-left">{s.name}</span>
                          <Badge variant="outline">{count}</Badge>
                        </Button>
                      );
                    })}
                </div>
              </CardContent>
            </Card>

            {/* Tasks column */}
            <Card className="md:col-span-2">
              <CardHeader>
                <CardTitle className="text-sm">
                  Completed Tasks {selectedStaffId !== 'all' ? `– ${getStaffName(selectedStaffId)}` : ''}
                </CardTitle>
              </CardHeader>
              <CardContent>
                {visibleTodos.length === 0 ? (
                  <div className="text-center text-muted-foreground py-8">No completed tasks found.</div>
                ) : (
                  <div className="space-y-3">
                    {paginatedTodos.map((todo) => (
                      <div key={todo.id} className="flex flex-col p-3 border rounded-md gap-3">
                        {/* Task Info Row */}
                        <div className="flex items-center justify-between">
                          <div className="flex-1 pr-4">
                            <div className="font-medium truncate">{todo.title}</div>
                            <div className="text-xs text-muted-foreground mt-1 flex gap-2 items-center flex-wrap">
                              {selectedStaffId === 'all' && (
                                <Badge variant="outline" className="text-[10px]">
                                  {getStaffName(todo.staff_id)}
                                </Badge>
                              )}
                              {todo.completed_at && (
                                <span>Completed: {format(new Date(todo.completed_at), 'MMM d, yyyy')}</span>
                              )}
                              {isTaskLate(todo) && (
                                <>
                                  <Badge variant="destructive" className="text-[10px]">
                                    Late ({calculateDaysLate(todo)} day{calculateDaysLate(todo) !== 1 ? 's' : ''})
                                  </Badge>
                                  {todo.late_reason && (
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      className="h-5 w-5 p-0"
                                      onClick={() => setLateReasonModal({ show: true, task: todo })}
                                      title="View late reason"
                                    >
                                      <Info className="h-3 w-3 text-blue-600" />
                                    </Button>
                                  )}
                                </>
                              )}
                            </div>
                          </div>
                          <div className="flex flex-col gap-1">
                            <div className="flex items-center gap-2">
                              <span className="text-xs text-muted-foreground">Rate:</span>
                              <RatingButtons value={(todo as any).rating ?? null} onChange={(n) => handleRate(todo, n)} />
                            </div>
                            {(todo as any).rating && (
                              <span className="text-[10px] text-muted-foreground italic">Click to change rating</span>
                            )}
                          </div>
                        </div>

                        {/* Reviewer Comment Section - Show when rating exists or when editing */}
                        {((todo as any).rating || editingTaskId === todo.id) && (
                          <div className="space-y-2 pt-2 border-t">
                            <Label className="text-xs font-medium">
                              Reviewer Feedback
                              {(todo as any).rating && (todo as any).rating <= 2 && (
                                <span className="text-red-500 ml-1">* (Required for low ratings)</span>
                              )}
                            </Label>
                            <Textarea
                              value={editingTaskId === todo.id ? reviewerComment : ((todo as any).reviewer_comment || '')}
                              onChange={(e) => setReviewerComment(e.target.value)}
                              onFocus={() => {
                                setEditingTaskId(todo.id);
                                setReviewerComment((todo as any).reviewer_comment || '');
                              }}
                              onBlur={() => {
                                if (reviewerComment !== ((todo as any).reviewer_comment || '')) {
                                  handleUpdateReviewerComment(todo, reviewerComment);
                                }
                                setTimeout(() => setEditingTaskId(null), 200);
                              }}
                              placeholder="Provide feedback on performance (optional for ratings 3-5, encouraged for 1-2)..."
                              className="text-sm min-h-[80px]"
                              maxLength={1000}
                            />
                            <div className="flex justify-between items-center text-xs text-muted-foreground">
                              <span>
                                {(todo as any).reviewed_at && `Reviewed ${format(new Date((todo as any).reviewed_at), 'MMM d, yyyy')}`}
                              </span>
                              <span>{(editingTaskId === todo.id ? reviewerComment : ((todo as any).reviewer_comment || '')).length}/1000 characters</span>
                            </div>
                          </div>
                        )}
                      </div>
                    ))}

                    {/* Pagination Controls */}
                    <div className="flex items-center justify-between pt-2">
                      <div className="text-xs text-muted-foreground">
                        Showing {(page - 1) * pageSize + 1}
                        –{Math.min(page * pageSize, visibleTodos.length)} of {visibleTodos.length}
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={page <= 1}
                          onClick={() => setPage((p) => Math.max(1, p - 1))}
                        >
                          Previous
                        </Button>
                        <Badge variant="outline">Page {page} / {totalPages}</Badge>
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={page >= totalPages}
                          onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                        >
                          Next
                        </Button>
                      </div>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="report" className="mt-4">
          {/* Period filters */}
          <Card className="mb-4">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3 flex-wrap">
                <Select value={reportRangeType} onValueChange={(v) => setReportRangeType(v as any)}>
                  <SelectTrigger className="w-[180px]">
                    <SelectValue placeholder="Select period" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="7days">Last 7 days</SelectItem>
                    <SelectItem value="30days">Last 30 days</SelectItem>
                    <SelectItem value="custom">Custom</SelectItem>
                  </SelectContent>
                </Select>
                {reportRangeType === 'custom' && (
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline" className="w-[260px] justify-start">
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {reportCustom.from && reportCustom.to
                          ? `${format(reportCustom.from, 'MMM d, yyyy')} – ${format(reportCustom.to, 'MMM d, yyyy')}`
                          : 'Pick a date range'}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="p-0" align="start">
                      <Calendar
                        mode="range"
                        selected={reportCustom as any}
                        onSelect={(range: any) => setReportCustom({ from: range?.from, to: range?.to })}
                      />
                    </PopoverContent>
                  </Popover>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Rating-based performance report */}
          <RatingPerformanceReport todos={useMemo(() => {
            // Determine range
            const today = endOfDay(new Date());
            let from = startOfDay(subDays(today, 30));
            let to = today;
            if (reportRangeType === '7days') from = startOfDay(subDays(today, 7));
            if (reportRangeType === 'custom' && reportCustom.from && reportCustom.to) {
              from = startOfDay(reportCustom.from);
              to = endOfDay(reportCustom.to);
            }
            // Filter todos by completion in range (fallback to created_at)
            return staffTodos.filter((t) => {
              const baseDate = t.completed_at ? new Date(t.completed_at) : new Date(t.created_at);
              return isWithinInterval(baseDate, { start: from, end: to });
            });
          }, [staffTodos, reportRangeType, reportCustom])} staff={activeStaff} />
        </TabsContent>
      </Tabs>

      {/* Late Reason Modal */}
      <Dialog
        open={lateReasonModal.show}
        onOpenChange={(open) => {
          if (!open) setLateReasonModal({ show: false, task: null });
        }}
      >
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Late Completion Reason</DialogTitle>
          </DialogHeader>
          {lateReasonModal.task && (
            <div className="space-y-4 py-4">
              <div>
                <div className="font-medium">{lateReasonModal.task.title}</div>
                <div className="text-sm text-muted-foreground mt-2 space-y-1">
                  <div>Deadline: {lateReasonModal.task.deadline ? format(new Date(lateReasonModal.task.deadline), 'PPP') : 'N/A'}</div>
                  <div>Completed: {lateReasonModal.task.completed_at ? format(new Date(lateReasonModal.task.completed_at), 'PPP') : 'N/A'}</div>
                  <div className="text-red-600 font-medium">
                    {calculateDaysLate(lateReasonModal.task)} day{calculateDaysLate(lateReasonModal.task) !== 1 ? 's' : ''} late
                  </div>
                </div>
              </div>
              <div>
                <Label className="text-sm font-semibold">Reason Provided:</Label>
                <div className="mt-2 p-3 bg-muted rounded-md text-sm whitespace-pre-wrap">
                  {lateReasonModal.task.late_reason || 'No reason provided'}
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button onClick={() => setLateReasonModal({ show: false, task: null })}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TaskPerformanceReviewPage;
